/**
 * Activity chart component
 * Displays jobs activity over time using Recharts
 */

import { Card, Typography, Segmented, Empty, Skeleton } from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useState } from 'react';
import type { ActivityDataPoint } from '@/types/api';
import { usePreferencesStore } from '@/stores/usePreferencesStore';
import { CHART_COLORS } from '@/styles/theme';
import { formatNumber } from '@/utils/formatters';

interface ActivityChartProps {
  data?: ActivityDataPoint[];
  loading?: boolean;
}

type TimeRange = '7d' | '14d' | '30d';

export function ActivityChart({ data = [], loading }: ActivityChartProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');
  const { theme } = usePreferencesStore();
  const isDarkMode = theme === 'dark';

  // Filter data based on time range
  const getDaysFromRange = (range: TimeRange): number => {
    switch (range) {
      case '7d':
        return 7;
      case '14d':
        return 14;
      case '30d':
        return 30;
      default:
        return 7;
    }
  };

  const filteredData = data.slice(-getDaysFromRange(timeRange));

  const chartColors = {
    jobs: CHART_COLORS.primary,
    places: CHART_COLORS.success,
    grid: isDarkMode ? '#303030' : '#f0f0f0',
    text: isDarkMode ? '#8c8c8c' : '#595959',
    background: isDarkMode ? '#1f1f1f' : '#fff',
  };

  if (loading) {
    return (
      <Card title="Jobs Activity">
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card title="Jobs Activity">
        <Empty description="No activity data available" />
      </Card>
    );
  }

  return (
    <Card
      title="Jobs Activity"
      extra={
        <Segmented
          size="small"
          value={timeRange}
          onChange={(value) => setTimeRange(value as TimeRange)}
          options={[
            { label: '7 Days', value: '7d' },
            { label: '14 Days', value: '14d' },
            { label: '30 Days', value: '30d' },
          ]}
        />
      }
    >
      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          data={filteredData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={chartColors.grid}
            vertical={false}
          />
          <XAxis
            dataKey="date"
            tick={{ fill: chartColors.text, fontSize: 12 }}
            tickLine={{ stroke: chartColors.grid }}
            axisLine={{ stroke: chartColors.grid }}
            tickFormatter={(value) => {
              const date = new Date(value);
              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            }}
          />
          <YAxis
            yAxisId="left"
            tick={{ fill: chartColors.text, fontSize: 12 }}
            tickLine={{ stroke: chartColors.grid }}
            axisLine={{ stroke: chartColors.grid }}
            tickFormatter={(value) => formatNumber(value)}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fill: chartColors.text, fontSize: 12 }}
            tickLine={{ stroke: chartColors.grid }}
            axisLine={{ stroke: chartColors.grid }}
            tickFormatter={(value) => formatNumber(value)}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: chartColors.background,
              border: `1px solid ${chartColors.grid}`,
              borderRadius: 8,
            }}
            formatter={(value: number, name: string) => [
              formatNumber(value),
              name === 'jobs' ? 'Jobs' : 'Places',
            ]}
            labelFormatter={(label) => {
              const date = new Date(label);
              return date.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              });
            }}
          />
          <Legend
            wrapperStyle={{ paddingTop: 16 }}
            formatter={(value) => (
              <span style={{ color: chartColors.text }}>
                {value === 'jobs' ? 'Jobs Created' : 'Places Found'}
              </span>
            )}
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="jobs"
            stroke={chartColors.jobs}
            strokeWidth={2}
            dot={{ fill: chartColors.jobs, strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, strokeWidth: 2 }}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="places"
            stroke={chartColors.places}
            strokeWidth={2}
            dot={{ fill: chartColors.places, strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}

export default ActivityChart;
